import React from 'react';
import { styled, keyframes } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { async_softwareReleasesVersionDetailIssueList_FiltersAsync$key } from './__generated__/async_softwareReleasesVersionDetailIssueList_FiltersAsync.graphql';
import type { Filters } from './index.tsx';

const FiltersLazy = lazyForPaint<typeof Filters>(() =>
	import(
		/* webpackChunkName: "async-jira-software-releases-version-detail-issue-list-filters" */ './index'
	).then((mod) => mod.Filters),
);

export const Loading = (): React.JSX.Element => (
	<FilterBarLoadingWraper>
		<TextFilterSkeleton />
		<AssigneeFilterSkeleton />
		<DropdownFilterSkeleton />
		<DropdownFilterSkeleton />
	</FilterBarLoadingWraper>
);

interface Props {
	jiraVersion: async_softwareReleasesVersionDetailIssueList_FiltersAsync$key;
}

export const FiltersAsync = ({ jiraVersion }: Props): React.JSX.Element => {
	const jiraVersionData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment async_softwareReleasesVersionDetailIssueList_FiltersAsync on JiraVersion {
				...filters_softwareReleasesVersionDetailIssueList
			}
		`,
		jiraVersion,
	);

	return (
		<JSErrorBoundary
			fallback="flag"
			id="jira.software.releases.version.detail.issue.list.filters"
			packageName="jira-software-releases-version-detail-issue-list"
			teamName="jolt"
		>
			<Placeholder
				name="jira-software-releases-version-detail-issue-list-filters"
				fallback={<Loading />}
			>
				<FiltersLazy jiraVersion={jiraVersionData} />
			</Placeholder>
		</JSErrorBoundary>
	);
};

const skeletonKeyFrames = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '2000px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AnimationBaseDiv = styled.div({
	animationDelay: '0s',
	animationDuration: '2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
	backgroundImage: `linear-gradient( to right, ${token('color.skeleton')} 10%, ${token('color.skeleton.subtle')} 30%, ${token('color.skeleton')} 50% )`,
	backgroundRepeat: 'no-repeat',

	backgroundColor: token('color.skeleton'),
	animationName: skeletonKeyFrames,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterBarLoadingWraper = styled.div({
	height: '40px',
	width: '100%',
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFilterSkeleton = styled(AnimationBaseDiv)({
	borderRadius: token('radius.small'),
	width: '120px',
	height: '40px',
	marginRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AssigneeFilterSkeleton = styled(AnimationBaseDiv)({
	borderRadius: token('radius.full'),
	height: '32px',
	width: '32px',
	marginRight: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownFilterSkeleton = styled(AnimationBaseDiv)({
	borderRadius: token('radius.full'),
	height: '20px',
	width: '64px',
	marginRight: token('space.200'),
});
